import React, { useState, useEffect } from "react";
import axios from "../../api/axiosConfig";
import "../../index.css";
import Swal from "sweetalert2";
import MainLayout from "../MainLayout";

// Función para obtener la fecha actual en México
const getMexicoCurrentDate = () => {
  // Importante: NO usar offsets fijos ni toISOString() (UTC) porque puede cambiar el día.
  // Esto devuelve YYYY-MM-DD en zona horaria de México.
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`; // Formato 'YYYY-MM-DD'
};

const getWeekRangeFromMexicoISO = (mexicoISODate) => {
  // mexicoISODate: YYYY-MM-DD (ya en calendario de México)
  const [y, m, d] = mexicoISODate.split("-").map((n) => parseInt(n, 10));
  const utc = new Date(Date.UTC(y, m - 1, d));
  const day = utc.getUTCDay(); // 0=Dom..6=Sab
  const diffToMonday = (day + 6) % 7; // Lun=0
  const monday = new Date(utc);
  monday.setUTCDate(utc.getUTCDate() - diffToMonday);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const weekStart = monday.toISOString().slice(0, 10);
  const weekEnd = sunday.toISOString().slice(0, 10);
  return { weekStart, weekEnd };
};

const getMonthRangeFromMexicoISO = (mexicoISODate) => {
  const [y, m] = mexicoISODate.split("-").map((n, idx) => (idx < 2 ? parseInt(n, 10) : n));
  const year = parseInt(mexicoISODate.split("-")[0], 10);
  const month = parseInt(mexicoISODate.split("-")[1], 10);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  return {
    monthStart: first.toISOString().slice(0, 10),
    monthEnd: last.toISOString().slice(0, 10),
  };
};

const getPeriodoRange = (periodo, mexicoISODate) => {
  const p = (periodo || "semanal").toLowerCase();
  if (p === "diario") {
    return { start: mexicoISODate, end: mexicoISODate, inicio: mexicoISODate, label: "Día" };
  }
  if (p === "mensual") {
    const { monthStart, monthEnd } = getMonthRangeFromMexicoISO(mexicoISODate);
    return { start: monthStart, end: monthEnd, inicio: monthStart, label: "Mes" };
  }
  const { weekStart, weekEnd } = getWeekRangeFromMexicoISO(mexicoISODate);
  return { start: weekStart, end: weekEnd, inicio: weekStart, label: "Semana" };
};

const CajaClosure = () => {
  const [periodo, setPeriodo] = useState("semanal");
  const [date, setDate] = useState(getMexicoCurrentDate());
  const { start, end, inicio, label } = getPeriodoRange(periodo, date);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const idUser = localStorage.getItem("idUser");
  const name = localStorage.getItem("name");
  const lastName = localStorage.getItem("lastName");
  const secondLastName = localStorage.getItem("secondLastName");
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!idUser) {
        console.error("Error: idUser no está disponible en localStorage");
        return;
      }
      try {
        axios
          .get(
            `/cortesCaja/comprobacionPeriodo/${idUser}?tipo=${periodo}&inicio=${inicio}`
          )
          .then((response) => {
          if (response.data.data[0]) {
            Swal.fire({
              title: `¡Ya has registrado tu corte de este ${label.toLowerCase()}!`,
              text: `${label}: ${start} a ${end}`,
              icon: "success",
              confirmButtonColor: "#3085d6",
              confirmButtonText: "Regresar",
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) {
                window.location.href = "/dashboard";
              }
            });
          }
        });
      } catch (error) {
        console.error("Error fetching comprobation:", error);
      }

      try {
        let response = {};
        const branchId = localStorage.getItem("branchId");

        if (userType === "1") {
          // Encargado: corte por SUCURSAL (no por idEncargado), usando branchId
          response = await axios.get(
            `/cortesCaja/transaccionesSucursal/${branchId}?start=${start}&end=${end}`
          );
        } else {
          // Individual: solo transacciones creadas por el usuario
          response = await axios.get(`/cortesCaja/${idUser}?start=${start}&end=${end}`);
        }

        const transactionsData = response.data.data || [];
        setTransactions(transactionsData);

        // Calcular el total general sumando todos los importes
        const totalAmount = transactionsData.reduce(
          (sum, transaction) => sum + transaction.importe,
          0
        );
        setTotal(totalAmount);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    };

    fetchTransactions();
  }, [date, idUser, periodo, inicio, start, end]);

  // Función para filtrar transacciones por tipo de pago y calcular el total
  const filterTransactionsByType = (formaPago) => {
    return transactions.filter(
      (transaction) => transaction.formaPago === formaPago
    );
  };

  const calculateTotalByType = (formaPago) => {
    return filterTransactionsByType(formaPago).reduce(
      (sum, transaction) => sum + transaction.importe,
      0
    );
  };

  const efectivoTransactions = filterTransactionsByType("Efectivo");
  const creditoTransactions = filterTransactionsByType("Credito");
  const debitoTransactions = filterTransactionsByType("Debito");
  const transferenciaTransactions = filterTransactionsByType("Transferencia");

  const totalEfectivo = calculateTotalByType("Efectivo");
  const totalCredito = calculateTotalByType("Credito");
  const totalDebito = calculateTotalByType("Debito");
  const totalTransferencia = calculateTotalByType("Transferencia");

  const sendClosure = () => {
    Swal.fire({
      title: "¿Confirmar el corte de caja?",
      text: `${label}: ${start} a ${end}. Confirma el corte si validaste los totales correspondientes a tu caja con certeza.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d00",
      confirmButtonText: "Confirmar corte",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const nombreCreador = `${name} ${lastName} ${secondLastName}`;
        const corteCajaData = {
          idCreador: idUser,
          nombreCreador: nombreCreador,
          idSucursal: localStorage.getItem("branchId"),
          totalEfectivo: totalEfectivo.toFixed(2),
          totalCredito: totalCredito.toFixed(2),
          totalDebito: totalDebito.toFixed(2),
          totalTransferencia: totalTransferencia.toFixed(2),
          totalGeneral: total.toFixed(2),
          fechaCorte: inicio, // compat
          periodoTipo: periodo,
          periodoInicio: inicio,
          periodoFin: end,
          tipoUsuarioCreador: localStorage.getItem("userType"),
        };
        axios.post("/cortesCaja/crear", corteCajaData).then((response) => {
          if (response.data) {
            Swal.fire({
              title: `¡Corte de ${label.toLowerCase()} registrado exitósamente!`,
              icon: "success",
              confirmButtonColor: "#3085d6",
              confirmButtonText: "Regresar",
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) {
                window.location.href = "/dashboard";
              }
            });
          }
        });
      }
    });
  };

  return (
    <MainLayout>
      <div
        style={{
          justifyContent: "center",
          paddingLeft: "20px",
        }}
      >
        <p style={{ fontSize: "28px", fontWeight: "700" }}>
          {userType === "1"
            ? "Corte de Caja Sucursal"
            : "Corte de Caja Individual"}{" "}
          <br />
          <span style={{ fontWeight: "500", fontSize: "22px" }}>
            {name} {lastName}
          </span>{" "}
        </p>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ minWidth: "220px" }}>
            <label style={{ fontWeight: 700 }}>Periodo</label>
            <select
              className="form-control form-select"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              style={{ fontWeight: 700 }}
            >
              <option value="diario">Diario</option>
              <option value="semanal">Semanal</option>
              <option value="mensual">Mensual</option>
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 700 }}>Fecha de referencia</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-control"
              style={{
                width: "fit-content",
                textAlign: "center",
                fontWeight: "700",
              }}
            />
          </div>
          <div style={{ fontWeight: "700" }}>
            {label}: <span style={{ fontWeight: 500 }}>{start} a {end}</span>
          </div>
        </div>

        {/* Contenedor para las tablas en fila */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginTop: "30px",
          }}
        >
          {/* Tabla para efectivo */}
          <div>
            <h4>
              <b>Transacciones en Efectivo</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", width: "550px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Monto</th>
                    <th scope="col">Tipo de Pago</th>
                    <th scope="col">Facturado</th>
                    {userType === "1" ? (
                      <th scope="col">Persona en Caja</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {efectivoTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>
                        $
                        {transaction.importe.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.tipoPago === "total"
                          ? "Total"
                          : transaction.tipoPago}
                      </td>
                      <td>
                        {transaction.facturacion === "True" ? "Si" : "No"}
                      </td>
                      {userType === "1" ? (
                        <td>{transaction.nombreCreador}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h5>
              Total en Efectivo:{" "}
              <b>
                $
                {totalEfectivo.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </h5>
          </div>

          {/* Tabla para crédito */}
          <div>
            <h4>
              <b>Transacciones con Crédito</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", width: "550px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Monto</th>
                    <th scope="col">Tipo de Pago</th>
                    <th scope="col">Facturado</th>
                    {userType === "1" ? (
                      <th scope="col">Persona en Caja</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {creditoTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>
                        $
                        {transaction.importe.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.tipoPago === "total"
                          ? "Total"
                          : transaction.tipoPago}
                      </td>
                      <td>
                        {transaction.facturacion === "True" ? "Si" : "No"}
                      </td>
                      {userType === "1" ? (
                        <td>{transaction.nombreCreador}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h5>
              Total en Crédito:{" "}
              <b>
                $
                {totalCredito.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </h5>
          </div>

          {/* Tabla para débito */}
          <div>
            <h4>
              <b>Transacciones con Débito</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", width: "550px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Monto</th>
                    <th scope="col">Tipo de Pago</th>
                    <th scope="col">Facturado</th>
                    {userType === "1" ? (
                      <th scope="col">Persona en Caja</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {debitoTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>
                        $
                        {transaction.importe.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.tipoPago === "total"
                          ? "Total"
                          : transaction.tipoPago}
                      </td>
                      <td>
                        {transaction.facturacion === "True" ? "Si" : "No"}
                      </td>
                      {userType === "1" ? (
                        <td>{transaction.nombreCreador}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h5>
              Total en Débito:{" "}
              <b>
                $
                {totalDebito.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </h5>
          </div>

          {/* Tabla para Transferencia */}
          <div style={{ marginTop: "35px" }}>
            <h4>
              <b>Transacciones con Transferencia</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", width: "550px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Monto</th>
                    <th scope="col">Tipo de Pago</th>
                    <th scope="col">Facturado</th>
                    {userType === "1" ? (
                      <th scope="col">Persona en Caja</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {transferenciaTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>
                        $
                        {transaction.importe.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.tipoPago === "total"
                          ? "Total"
                          : transaction.tipoPago}
                      </td>
                      <td>
                        {transaction.facturacion === "True" ? "Si" : "No"}
                      </td>
                      {userType === "1" ? (
                        <td>{transaction.nombreCreador}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h5>
              Total en Transferencia:{" "}
              <b>
                $
                {totalTransferencia.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </h5>
          </div>
        </div>

        {/* Total general */}
        <h3>
          Total del {label}:{" "}
          <b>
            $
            {total.toLocaleString("es-MX", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </b>
        </h3>
        <button
          className="btn btn-success"
          style={{ margin: "50px auto", width: "fit-content" }}
          onClick={sendClosure}
        >
          Declarar corte
        </button>
      </div>
    </MainLayout>
  );
};

export default CajaClosure;
